/**
 * @file Root level chat page context that provides basic data about the current chat
 */

import { Character } from "@moe/priv/model/character";
import { Persona } from "@moe/priv/model/persona";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@web/components/LoadingSpinner";
import { useChatCharactersQuery } from "@web/route-services/chat/use-chat-characters-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { createContext, useContext } from "react";

/**
 * Data available in the data context
 * @param chatID The ID of the chat
 * @param chars The characters in the chat
 * @param persona The persona the user is using for the chat
 */
interface Props {
  chatID: number;
  chars: Map<number, Character>;
  persona: Persona;
}

const Context = createContext<Props>({} as Props);

export const useDataContext = () => {
  return useContext(Context);
};

/**
 * A context responsible for providing basic chat data to the rest of the chat route.
 *
 */
export const DataContextProvider = ({ chatID, children }: { chatID: number; children: React.ReactNode }) => {
  const { access } = useAppContext();

  const { data: characters, isPending: isCardPending, isError: isCardError } = useChatCharactersQuery(chatID);

  const {
    data: persona,
    isPending: isPersonaPending,
    isError: isPersonaError
  } = useQuery({
    queryKey: ["chatPersona", chatID],
    queryFn: async () => {
      return await access.persona.getByChatID(chatID);
    }
  });

  if (isCardPending || isPersonaPending)
    return (
      <div className=" flex size-full items-center justify-center ">
        <LoadingSpinner />{" "}
      </div>
    );
  if (isCardError || isPersonaError) return <div>{"Error loading card and persona :<"}</div>;

  return (
    <Context.Provider
      value={{
        chatID,

        chars: characters,
        persona
      }}
    >
      {children}
    </Context.Provider>
  );
};
